<template>
    <div>
        <!-- Discount list -->
        <div v-for="(discount, index) in localDiscounts" :key="index" class="discount-item">
            <div class="box">
                <div class="row">
                    <div class="column is-6">
                        <h4>{{ getInvoiceLabel(index) }}</h4>
                    </div>
                    <div class="column is-6 text-right">
                        <b-button
                            type="is-danger"
                            icon-left="trash-alt"
                            @click="removeDiscount(index)"
                            :disabled="localDiscounts.length === 1 || disabled"
                        />
                    </div>
                </div>

                <!-- Apply to all toggle - only show for first discount when there's only one and not at max 1 discount -->
                <div class="row" v-if="index === 0 && localDiscounts.length === 1 && !disabled && maxDiscounts > 1">
                    <div class="column">
                        <b-field>
                            <b-radio v-model="localApplyToAll" :native-value="true">
                                Toepassen op alle facturen
                            </b-radio>
                        </b-field>
                        <b-field>
                            <b-radio v-model="localApplyToAll" :native-value="false">
                                Alleen toepassen op de eerste factuur
                            </b-radio>
                        </b-field>
                    </div>
                </div>

                <div class="row">
                    <div class="column is-6">
                        <b-field label="Type">
                            <b-select v-model="discount.type" expanded :disabled="disabled">
                                <option value="percentage">Percentage</option>
                                <option value="fixed">Vast bedrag</option>
                            </b-select>
                        </b-field>
                    </div>
                    <div class="column is-6">
                        <b-field :label="discount.type === 'percentage' ? 'Percentage' : 'Bedrag (€)'">
                            <b-input
                                v-model.number="discount.value"
                                type="number"
                                step="0.01"
                                min="0"
                                :placeholder="discount.type === 'percentage' ? '10' : '5.00'"
                                :disabled="disabled"
                            >
                                <template #prepend>
                                    <b-icon :icon="discount.type === 'percentage' ? 'percent' : 'euro-sign'"></b-icon>
                                </template>
                            </b-input>
                        </b-field>
                    </div>
                    <div class="column is-12" v-if="showDescription">
                        <b-field label="Omschrijving">
                            <b-input
                                v-model="discount.description"
                                placeholder="Optioneel"
                                :disabled="disabled"
                            ></b-input>
                        </b-field>
                    </div>
                </div>

                <!-- Calculation/Preview - only show if productPrice is provided -->
                <div class="row" v-if="discount.value > 0 && productPrice > 0">
                    <div class="column">
                        <b-notification type="is-light" size="is-small">
                            <strong>{{ productPrice ? 'Berekening' : 'Voorbeeld' }}:</strong>
                            Productprijs: €{{ productPrice.toFixed(2).replace('.', ',') }}
                            - Korting: {{ getDiscountDisplay(discount) }}
                            <span v-if="productPrice">({{ getDiscountAmount(discount) }})</span>
                            = <strong>€{{ calculateDiscountedPrice(discount).toFixed(2).replace('.', ',') }}</strong>
                        </b-notification>
                    </div>
                </div>

            </div>
        </div>

        <!-- Add more discounts button -->
        <div class="has-text-centered margintop20" v-if="!disabled && localDiscounts.length < maxDiscounts">
            <b-button
                type="is-primary"
                icon-left="plus"
                @click="addDiscount"
            >
                Voeg korting toe voor {{ getNextInvoiceLabel() }}
            </b-button>
        </div>

        <hr v-if="showHelpText">

        <p class="help" v-if="showHelpText">
            <strong>Let op:</strong>
            <span v-if="localDiscounts.length === 1">
                Kies hierboven of de korting voor alle facturen geldt, of alleen voor de eerste factuur.
            </span>
            <span v-else-if="localDiscounts.length > 1">
                Deze kortingen gelden specifiek per factuurnummer (1e, 2e, 3e, etc.).
            </span>
        </p>
    </div>
</template>

<script>
export default {
    name: 'InvoiceDiscountManager',

    props: {
        discounts: {
            type: Array,
            required: true
        },
        applyToAll: {
            type: Boolean,
            default: true
        },
        productPrice: {
            type: Number,
            default: 0
        },
        disabled: {
            type: Boolean,
            default: false
        },
        showDescription: {
            type: Boolean,
            default: true
        },
        showHelpText: {
            type: Boolean,
            default: true
        },
        maxDiscounts: {
            type: Number,
            default: 12
        }
    },

    data() {
        return {
            localDiscounts: [...this.discounts],
            localApplyToAll: this.applyToAll
        };
    },

    watch: {
        discounts: {
            deep: true,
            handler(newVal) {
                if (JSON.stringify(newVal) !== JSON.stringify(this.localDiscounts)) {
                    this.localDiscounts = [...newVal];
                }
            }
        },
        applyToAll(newVal) {
            if (newVal !== this.localApplyToAll) {
                this.localApplyToAll = newVal;
            }
        },
        localDiscounts: {
            deep: true,
            handler(newVal) {
                this.$emit('update:discounts', newVal);
            }
        },
        localApplyToAll(newVal) {
            this.$emit('update:applyToAll', newVal);
        }
    },

    methods: {
        createEmptyDiscount() {
            return {
                type: 'percentage',
                value: 0,
                description: '',
                invoice_number: null
            };
        },

        addDiscount() {
            this.localDiscounts.push(this.createEmptyDiscount());
            // When adding a second discount, disable apply to all
            if (this.localDiscounts.length > 1) {
                this.localApplyToAll = false;
            }
        },

        removeDiscount(index) {
            if (this.localDiscounts.length > 1) {
                this.localDiscounts.splice(index, 1);
            }
        },

        getInvoiceLabel(index) {
            if (this.localDiscounts.length === 1) {
                return this.localApplyToAll ? 'Alle facturen' : '1e factuur';
            }
            if (index === 0) return '1e factuur';
            if (index === 1) return '2e factuur';
            if (index === 2) return '3e factuur';
            return `${index + 1}e factuur`;
        },

        getNextInvoiceLabel() {
            const nextIndex = this.localDiscounts.length;
            if (nextIndex === 0) return '1e factuur';
            if (nextIndex === 1) return '2e factuur';
            if (nextIndex === 2) return '3e factuur';
            return `${nextIndex + 1}e factuur`;
        },

        getDiscountDisplay(discount) {
            if (discount.type === 'percentage') {
                return `${discount.value}%`;
            }
            return `€${Number(discount.value).toFixed(2).replace('.', ',')}`;
        },

        getDiscountAmount(discount) {
            if (!this.productPrice) return '';

            let discountAmount = 0;

            if (discount.type === 'percentage') {
                discountAmount = this.productPrice * (discount.value / 100);
            } else {
                discountAmount = Math.min(discount.value, this.productPrice);
            }

            return `-€${discountAmount.toFixed(2).replace('.', ',')}`;
        },

        calculateDiscountedPrice(discount) {
            const basePrice = this.productPrice || 50; // Use 50 as example if no price provided
            let discountAmount = 0;

            if (discount.type === 'percentage') {
                discountAmount = basePrice * (discount.value / 100);
            } else {
                discountAmount = Math.min(discount.value, basePrice);
            }

            return Math.max(0, basePrice - discountAmount);
        }
    }
};
</script>

<style lang="scss" scoped>
.discount-item {
    margin-bottom: 1rem;
}

.box {
    overflow: auto;
    display: block;
    background-color: #e1e1e1;
    border: 1px solid #c1c1c1;
}
</style>
